import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  ClrCheckboxModule,
  ClrDatagridModule,
  ClrDropdownModule,
  ClrInputModule,
  ClrModalModule,
  ClrNumberInputModule,
  ClrSelectModule,
  ClrStepperModule
} from "@clr/angular";
import { ClarityIcons, twoWayArrowsIcon, eyeIcon, timesCircleIcon, plusIcon, minusIcon } from "@cds/core/icon";
import { debounceTime, distinctUntilChanged, filter, Subject, switchMap } from "rxjs";
import { Components, Files, MaterialModel } from "../../../_shared/interfaces";
import { takeUntil, tap } from "rxjs/operators";
import { AlertService, ComponentsStoreService, IAlertType, MaterialStoreService } from "../../../_shared/services";
import { Router, RouterLink } from "@angular/router";
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { UploadComponent } from '../../../core/upload/upload.component';
import { ComponentFilesComponent } from "../component-files/component-files.component";

import { environment as env } from "../../../../environments/environment";

@Component({
  selector: 'app-component-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ClrStepperModule,
    ClrInputModule,
    ClrDatagridModule,
    ClrDropdownModule,
    ClrCheckboxModule,
    ClrModalModule,
    ClrSelectModule,
    RouterLink,
    NgxExtendedPdfViewerModule,
    UploadComponent,
    ClrNumberInputModule,
    ComponentFilesComponent
  ],
  templateUrl: './component-form.component.html',
  styleUrl: './component-form.component.sass'
})
export class ComponentFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isUpdateMode = false;
  @Input() componentData?: Components;
  @Output() formSubmit = new EventEmitter<Components>();

  componentForm!: FormGroup;
  currentComponent: string = '';
  componentsFiles: Files[] = [];
  materials: MaterialModel[] = [];
  loading = true;
  componentExists = false;
  devMode = false;
  isCreating = false;
  isCreatedInWizard = false;

  private keyUp$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private compss: ComponentsStoreService,
    private materialss: MaterialStoreService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
    private router: Router
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon, eyeIcon, timesCircleIcon, plusIcon, minusIcon);
    this.devMode = env.stages.dev;
    this.initForm();
  }

  ngOnInit() {
    this.loadMaterials();
    if (this.isUpdateMode && this.componentData) {
      this.currentComponent = this.componentData.technical_drawing;
      this.patchFormValues(this.componentData);
      this.loadComponentFiles();
    }

    this.setupSearch();
    this.setupMaterialSync();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reagiere auf spätes Eintreffen der @Input()-Daten (z. B. per API-Call im Parent)
    if (changes['componentData'] && this.isUpdateMode && changes['componentData'].currentValue) {
      const data = changes['componentData'].currentValue as Components;
      this.currentComponent = data.technical_drawing;
      this.patchFormValues(data);
      // Zeichnungsnummer im Update-Modus gegen Änderungen absichern (per readonly im Template)
      this.loadComponentFiles();
    }

    // Falls der Update-Modus erst nach Form-Init gesetzt wurde, Feld deaktivieren
    // (nicht mehr deaktivieren, da dies den Clarity Stepper an der Validierung hindern kann)
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.componentForm = this.fb.group({
      stepI: this.fb.group({
        technical_drawing: ['', [Validators.required, Validators.maxLength(100)]]
      }),
      stepII: this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(255)]],
        description: [''],
        specification: [''],
        material: [''],
        material_id: [null],
        manufacturer: [''],
        guideline_price: [null, [Validators.min(0)]],
        weight: [null],
        taric: ['', [Validators.pattern('^(\\d{8}|\\d{10})$')]],
        number_of_pieces_in_stock: [0, [Validators.required, Validators.min(0)]],
        technology: [false],
        standard: [false],
        active: [true]
      }),
      stepIII: this.fb.group({})
    });
    // Im Update-Modus soll die Zeichnungsnummer nicht geändert werden können -> per [readonly] im Template gelöst
  }

  private patchFormValues(data: Components) {
    if (!data) return;

    const normalizedTaric = this.normalizeTaricForForm(data.taric);

    const formValue = {
      stepI: {
        technical_drawing: data.technical_drawing
      },
      stepII: {
        name: data.name,
        description: data.description || '',
        specification: data.specification || '',
        material: data.material_name || data.material || '',
        material_id: data.material_id ?? null,
        manufacturer: data.manufacturer || '',
        guideline_price: data.guideline_price ?? null,
        weight: data.weight || null,
        taric: normalizedTaric,
        number_of_pieces_in_stock: data.number_of_pieces_in_stock ?? data.number_of_pieces ?? 0,
        technology: data.technology || false,
        standard: data.standard || false,
        active: data.active !== undefined ? data.active : true
      }
    };

    this.componentForm.patchValue(formValue);
  }

  private normalizeTaricForForm(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized || normalized === '0') {
      return '';
    }
    return normalized;
  }

  private setupSearch() {
    this.keyUp$.pipe(
      // Ab dem ersten Zeichen pruefen: die Abfrage ist seit der Umstellung auf
      // einen Primaerschluesselzugriff praktisch kostenlos, und die frueheren
      // drei Zeichen liessen kurze Zeichnungsnummern ungeprueft durch.
      filter(term => term.trim().length >= 1),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.loading = true),
      switchMap(searchTerm => this.compss.searchDB(searchTerm, 'all')),
      tap(() => this.loading = false)
    ).subscribe({
      next: (components) => {
        if (components?.object) {
          this.componentExists = components.object.length > 0;
          if (this.componentExists && !this.isUpdateMode) {
            this.alertDispatcher.show({
              message: 'Diese Zeichnungsnummer existiert bereits!',
              clrAlertType: IAlertType.WARNING
            }, 2000);
          }
        }
      },
      error: (error) => {
        console.error('Error searching components:', error);
        this.alertDispatcher.show({
          message: 'Fehler bei der Komponentensuche: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 2000);
      }
    });
  }

  private setupMaterialSync() {
    const control = this.componentForm.get('stepII.material_id');
    if (!control) return;

    control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((materialId) => {
        const name = this.resolveMaterialName(materialId);
        this.componentForm.get('stepII.material')?.setValue(name ?? '', { emitEvent: false });
      });
  }

  private resolveMaterialName(materialId: number | null | undefined): string | null {
    if (!materialId || !this.materials.length) {
      return null;
    }
    const match = this.materials.find((m) => m.material_id === materialId);
    return match?.material ?? null;
  }

  private loadMaterials() {
    this.materialss.getMaterials().subscribe({
      next: (response) => {
        this.materials = response?.data ?? [];
        const selectedId = this.componentForm.get('stepII.material_id')?.value;
        const name = this.resolveMaterialName(selectedId);
        if (name) {
          this.componentForm.get('stepII.material')?.setValue(name, { emitEvent: false });
        }
      },
      error: (error) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Materialien: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 2000);
      }
    });
  }

  onKeyUp(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (!this.isUpdateMode) {
      this.keyUp$.next(value);
    }
  }

  loadComponentFiles() {
    if (this.currentComponent) {
      this.compss.getComponentsFile([1, 2, 3], '', this.currentComponent, 0, 0)
        .subscribe({
          next: (response) => {
            if (response?.object) {
              this.componentsFiles = response.object;
            }
          },
          error: (error) => {
            this.alertDispatcher.show({
              message: 'Fehler beim Laden der Dateien: ' + error,
              clrAlertType: IAlertType.DANGER
            }, 2000);
          }
        });
    }
  }

  onSubmit() {
    if (!this.componentForm.valid) {
      return;
    }

    const formValue = this.buildFormValue();
    if (!formValue) {
      return;
    }

    if (this.isUpdateMode) {
      this.compss.updateComponent(formValue).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Komponente erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(formValue);
          this.router.navigate(['/component/details', formValue.technical_drawing]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren der Komponente: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
      return;
    }

    if (!this.isCreatedInWizard) {
      this.createComponentForUploads(formValue);
      return;
    }

    this.formSubmit.emit(formValue);
  }

  private createComponentForUploads(formValue: Components) {
    this.isCreating = true;
    this.compss.addComponent(formValue).subscribe({
      next: () => {
        this.currentComponent = formValue.technical_drawing;
        this.isCreatedInWizard = true;
        this.loadComponentFiles();
        this.alertDispatcher.show({
          message: 'Komponente erstellt. Jetzt Dateien hochladen oder Fertig klicken.',
          clrAlertType: IAlertType.SUCCESS
        }, 5000);
      },
      error: (error) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Erstellen der Komponente: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      },
      complete: () => {
        this.isCreating = false;
      }
    });
  }

  private buildFormValue(): Components | null {
    const raw = this.componentForm.getRawValue();
    const materialName = this.resolveMaterialName(raw.stepII?.material_id);
    if (materialName != null) {
      raw.stepII.material = materialName;
    }

    const technicalDrawing = this.isUpdateMode
      ? (this.currentComponent || raw.stepI?.technical_drawing)
      : (this.currentComponent || raw.stepI?.technical_drawing);
    if (!technicalDrawing) {
      return null;
    }

    return {
      technical_drawing: technicalDrawing,
      ...raw.stepII
    } as Components;
  }
}
